import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@avs/contract';

export type ApiCallState<T> = {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refresh: () => void;
};

/**
 * 极简数据 hook：mount + 依赖变化时调一次，提供 refresh。
 * 不上 react-query；P0 阶段够用，复杂度可控。
 */
export function useApiCall<T>(
  fn: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
): ApiCallState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);
  const cancelledRef = useRef(false);

  const run = useCallback(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (!cancelledRef.current) { setData(d); setLoading(false); } })
      .catch((e: unknown) => {
        if (cancelledRef.current) return;
        const err = e instanceof ApiError
          ? e
          : new ApiError('INTERNAL', e instanceof Error ? e.message : 'unknown');
        setError(err);
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => { cancelledRef.current = true; };
  }, [run]);

  return { data, loading, error, refresh: run };
}
